'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function LiveChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Array<{ text: string; sender: 'user' | 'bot'; timestamp: Date }>>([{
    text: 'Hi! How can we help you today? Feel free to ask about our services, pricing, or schedule a cleaning!',
    sender: 'bot',
    timestamp: new Date()
  }]);

  const handleSendMessage = () => {
    if (!message.trim()) return;

    // Add user message
    const userMessage = {
      text: message,
      sender: 'user' as const,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    // Simple auto-response logic
    setTimeout(() => {
      let botResponse = '';
      const lowerMessage = message.toLowerCase();

      if (lowerMessage.includes('price') || lowerMessage.includes('cost') || lowerMessage.includes('pricing')) {
        botResponse = 'Our pricing starts at $199 for Standard Clean, $349 for Deep Clean, $469 for Move-In/Out, and $249 for Office Cleaning. Would you like to book a service?';
      } else if (lowerMessage.includes('book') || lowerMessage.includes('schedule') || lowerMessage.includes('appointment')) {
        botResponse = 'Great! You can book directly on our homepage. Click the "Book Now" button and select your preferred service, date, and time. Need help with anything else?';
      } else if (lowerMessage.includes('service') || lowerMessage.includes('clean')) {
        botResponse = 'We offer Deep Cleaning, Standard Cleaning, Move-In/Out Cleaning, and Office Cleaning services. All services use eco-friendly products. Which service are you interested in?';
      } else if (lowerMessage.includes('area') || lowerMessage.includes('location') || lowerMessage.includes('where')) {
        botResponse = 'We serve the entire Twin Cities metro area including Minneapolis, St. Paul, and surrounding suburbs. Check our Service Areas page for the full list!';
      } else if (lowerMessage.includes('contact') || lowerMessage.includes('phone') || lowerMessage.includes('email')) {
        botResponse = 'You can reach us at (612) 555-1234 or email info@gellecleaning.com. Our business hours are Mon-Fri 8AM-6PM, Sat 9AM-5PM. How else can I help?';
      } else {
        botResponse = 'Thank you for your message! For detailed assistance, please contact us at (612) 555-1234 or visit our Contact page. Is there anything specific I can help you with?';
      }

      const botMessage = {
        text: botResponse,
        sender: 'bot' as const,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    }, 1000);

    setMessage('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 bg-green-600 text-white rounded-full shadow-2xl hover:bg-green-700 transition-all hover:scale-110 flex items-center justify-center z-50"
          aria-label="Open chat"
        >
          <Icon name="ChatBubbleLeftRightIcon" size={28} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Icon name="ChatBubbleLeftRightIcon" size={20} />
              </div>
              <div>
                <h3 className="font-semibold">Gelle Cleaning</h3>
                <p className="text-xs text-white/80">Online now</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              aria-label="Close chat"
            >
              <Icon name="XMarkIcon" size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.sender === 'user' ?'bg-green-600 text-white rounded-br-none' :'bg-gray-100 text-gray-800 rounded-bl-none'
                  }`}
                >
                  <p className="text-sm">{msg.text}</p>
                  <p className="text-xs mt-1 opacity-70">
                    {msg.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              />
              <button
                onClick={handleSendMessage}
                disabled={!message.trim()}
                className="w-10 h-10 bg-green-600 text-white rounded-full hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                aria-label="Send message"
              >
                <Icon name="PaperAirplaneIcon" size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}